import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { bookmarksApi } from '../lib/api'
import type { Bookmark } from '../types'

interface UseBookmarksOptions {
  autoLoad?: boolean
}

export function useBookmarks(options: UseBookmarksOptions = {}) {
  const { autoLoad = true } = options

  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadBookmarks = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await bookmarksApi.list()
      setBookmarks(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookmarks')
    } finally {
      setLoading(false)
    }
  }, [])

  const createBookmark = useCallback(async (
    surahNumber: number,
    ayahNumber: number,
    note?: string,
    tags?: string[]
  ) => {
    try {
      setError(null)
      const bookmark = await bookmarksApi.create({ surahNumber, ayahNumber, note, tags })
      setBookmarks(prev => [bookmark, ...prev])
      return bookmark
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create bookmark'
      setError(message)
      throw err
    }
  }, [])

  const updateBookmark = useCallback(async (
    bookmarkId: string,
    updates: { note?: string; tags?: string[] }
  ) => {
    try {
      setError(null)
      const updated = await bookmarksApi.update(bookmarkId, updates)
      setBookmarks(prev => prev.map(b => b.bookmarkId === bookmarkId ? updated : b))
      return updated
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update bookmark'
      setError(message)
      throw err
    }
  }, [])

  const deleteBookmark = useCallback(async (bookmarkId: string) => {
    try {
      setError(null)
      await bookmarksApi.delete(bookmarkId)
      setBookmarks(prev => prev.filter(b => b.bookmarkId !== bookmarkId))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete bookmark'
      setError(message)
      throw err
    }
  }, [])

  // Index bookmarks by "surah:ayah" key for O(1) lookups. Without this each
  // call to isBookmarked / getBookmark / toggleBookmark scanned the entire
  // array — and the reader calls isBookmarked once per rendered verse, so a
  // surah with N verses paid O(N²) per render. With this Map the same work
  // is O(N) once per bookmark-list change, then O(1) per lookup.
  const bookmarksByKey = useMemo(() => {
    const m = new Map<string, Bookmark>()
    for (const b of bookmarks) m.set(`${b.surahNumber}:${b.ayahNumber}`, b)
    return m
  }, [bookmarks])

  // Stable callback identities. The closures read from a ref so they don't
  // need to list `bookmarksByKey` as a dep — that lets React.memo'd children
  // that receive these callbacks skip re-renders when the bookmark list
  // changes (their behavior still uses the latest map via the ref).
  const bookmarksByKeyRef = useRef(bookmarksByKey)
  bookmarksByKeyRef.current = bookmarksByKey

  const isBookmarked = useCallback((surahNumber: number, ayahNumber: number) => {
    return bookmarksByKeyRef.current.has(`${surahNumber}:${ayahNumber}`)
  }, [])

  const getBookmark = useCallback((surahNumber: number, ayahNumber: number) => {
    return bookmarksByKeyRef.current.get(`${surahNumber}:${ayahNumber}`)
  }, [])

  const toggleBookmark = useCallback(async (
    surahNumber: number,
    ayahNumber: number
  ) => {
    const existing = bookmarksByKeyRef.current.get(`${surahNumber}:${ayahNumber}`)
    if (existing) {
      await deleteBookmark(existing.bookmarkId)
      return null
    } else {
      return await createBookmark(surahNumber, ayahNumber)
    }
  }, [deleteBookmark, createBookmark])

  useEffect(() => {
    if (autoLoad) {
      loadBookmarks()
    }
  }, [autoLoad, loadBookmarks])

  return {
    bookmarks,
    loading,
    error,
    loadBookmarks,
    createBookmark,
    updateBookmark,
    deleteBookmark,
    isBookmarked,
    getBookmark,
    toggleBookmark
  }
}
